import { useMemo, useState } from "react";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = [
  { value: "customer_service", label: "Customer service" },
  { value: "thank_you", label: "Thank you" },
  { value: "gift", label: "Gift" },
  { value: "promo", label: "Promotional" },
  { value: "other", label: "Other" },
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseUserIds(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tok of raw.split(/[\s,;]+/)) {
    const t = tok.trim().toLowerCase();
    if (!t || !UUID_RE.test(t) || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

type ResultRow = {
  user_id: string;
  ok: boolean;
  ledger_id?: string;
  balance_after?: number;
  error?: string;
};

type BulkResponse = {
  ok?: boolean;
  batch_id?: string;
  succeeded?: number;
  failed?: number;
  results?: ResultRow[];
  error?: string;
};

export function BulkGrantCreditsPanel() {
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [amount, setAmount] = useState("100");
  const [category, setCategory] = useState("thank_you");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{
    batchId: string;
    succeeded: number;
    failed: number;
    rows: ResultRow[];
  } | null>(null);

  const targets = useMemo(() => parseUserIds(userIdsRaw), [userIdsRaw]);
  const amtNum = Number(amount);
  const amtValid =
    Number.isInteger(amtNum) && amtNum >= 1 && amtNum <= 10_000;
  const reasonValid =
    reason.trim().length >= 10 && reason.trim().length <= 500;
  const valid = targets.length > 0 && amtValid && reasonValid;
  const totalCredits = targets.length * (amtValid ? amtNum : 0);

  const run = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "admin-grant-credits",
        {
          body: {
            op: "bulk_grant",
            user_ids: targets,
            amount: amtNum,
            category,
            reason: reason.trim(),
          },
        },
      );
      if (error) throw new Error(error.message);
      const payload = data as BulkResponse;
      if (payload?.error) throw new Error(payload.error);

      const succeeded = payload.succeeded ?? 0;
      const failed = payload.failed ?? 0;
      setResults({
        batchId: payload.batch_id ?? "",
        succeeded,
        failed,
        rows: payload.results ?? [],
      });
      const totalGranted = succeeded * amtNum;
      toast.success(
        `Granted ${totalGranted.toLocaleString()} credits to ${succeeded} user(s)` +
          (failed > 0 ? ` · ${failed} failed` : ""),
      );
      if (failed > 0) {
        console.warn(
          "Bulk credit grant: per-user failures",
          (payload.results ?? []).filter((r) => !r.ok),
        );
      }
      setOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bulk grant failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" /> Bulk credit grants
        </CardTitle>
        <CardDescription>
          Grant the same amount, category, and reason to a list of users in one
          shot. Every grant is logged individually and linked by a shared batch
          ID.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>User IDs (comma, newline, or space separated)</Label>
          <Textarea
            value={userIdsRaw}
            onChange={(e) => setUserIdsRaw(e.target.value)}
            rows={3}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {targets.length} valid UUID{targets.length === 1 ? "" : "s"}{" "}
            detected (max 200).
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="bulk-grant-amount">Amount per user</Label>
            <Input
              id="bulk-grant-amount"
              type="number"
              min={1}
              max={10000}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              1 to 10,000 credits each.
            </p>
          </div>
          <div>
            <Label htmlFor="bulk-grant-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="bulk-grant-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="bulk-grant-reason">Reason (required)</Label>
          <Textarea
            id="bulk-grant-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Thank-you to early beta users for week-one feedback."
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {reason.trim().length}/500 · minimum 10 characters
          </p>
        </div>

        <div className="rounded-md border border-border bg-muted/40 p-2 text-xs">
          Total to grant:{" "}
          <span className="font-semibold text-foreground">
            {totalCredits.toLocaleString()} credits
          </span>{" "}
          across {targets.length} user{targets.length === 1 ? "" : "s"}.
        </div>

        <Button disabled={!valid || busy} onClick={() => setOpen(true)}>
          <Sparkles className="h-4 w-4" />
          Grant {amtValid ? amtNum.toLocaleString() : "…"} credits to{" "}
          {targets.length || 0} user{targets.length === 1 ? "" : "s"}
        </Button>

        {results && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="default">
                {results.succeeded} succeeded
              </Badge>
              {results.failed > 0 && (
                <Badge variant="destructive">{results.failed} failed</Badge>
              )}
              {results.batchId && (
                <span className="text-muted-foreground">
                  Batch <code className="font-mono">{results.batchId}</code>
                </span>
              )}
            </div>
            <ul className="max-h-64 space-y-1 overflow-auto text-xs">
              {results.rows.map((r) => (
                <li
                  key={r.user_id}
                  className="flex items-start justify-between gap-2 border-b border-border/40 py-1 last:border-b-0"
                >
                  <span className="truncate font-mono">{r.user_id}</span>
                  {r.ok ? (
                    <span className="text-muted-foreground">
                      ok · balance {r.balance_after?.toLocaleString() ?? "?"}
                    </span>
                  ) : (
                    <span className="text-destructive">{r.error}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm bulk credit grant</AlertDialogTitle>
            <AlertDialogDescription>
              This will grant{" "}
              <b>{amtValid ? amtNum.toLocaleString() : "?"}</b> credits to{" "}
              <b>{targets.length}</b> user(s) — total{" "}
              <b>{totalCredits.toLocaleString()}</b> credits. Granted credits
              never expire and roll over with the user's balance. Type{" "}
              <b>GRANT</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="GRANT"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "GRANT" || busy}
              onClick={(e) => {
                e.preventDefault();
                void run();
              }}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}{" "}
              Confirm grant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
